import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiAnagramGenerate } from '../utils/api';
import { DICTIONARIES, loadDict, saveDict } from '../utils/dicts';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import DictInfo from '../components/DictInfo';
import PageSEO from '../components/PageSEO';

const EXAMPLES = ['CINEMA', 'DORMITORY', 'ELVIS', 'CLINT EASTWOOD', 'ASTRONOMER'];
const cleanInput = s => s.replace(/[^a-zA-Z ]/g, '');
const lettersOnly = s => s.toLowerCase().replace(/[^a-z]/g, '');

export default function AnagramGenerator() {
  const [text, setText]         = useState('');
  const [dict, setDict]         = useState(loadDict);
  const [mode, setMode]         = useState('standard');   // 'standard' | 'funny'
  const [multiword, setMulti]   = useState(true);
  const [data, setData]         = useState(null);
  const [usedMulti, setUsedMulti] = useState(true);
  const [usedFunny, setUsedFunny] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [copied, setCopied]     = useState('');

  async function handleGenerate(e) {
    e.preventDefault();
    if (!lettersOnly(text)) return;
    const funny = mode === 'funny';
    setLoading(true); setError(''); setData(null);
    setUsedMulti(funny || multiword); setUsedFunny(funny);
    try {
      const { data } = await apiAnagramGenerate(text, dict, multiword, mode);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setLoading(false); }
  }

  function changeDict(next) { setDict(next); saveDict(next); }

  function copy(str, key) {
    if (navigator.clipboard) navigator.clipboard.writeText(str);
    setCopied(key);
    setTimeout(() => setCopied(''), 1200);
  }

  function copyAll() {
    const all = [
      ...(data?.exact || []).map(r => r.word.toUpperCase()),
      ...(data?.phrases || []).map(p => p.toUpperCase()),
    ].join('\n');
    copy(all, '__all__');
  }

  const exact   = data?.exact || [];
  const phrases = data?.phrases || [];
  const subwords = data?.subwords || [];
  const nothing = data && exact.length === 0 && phrases.length === 0;

  return (
    <div className="game-page">
      <PageSEO page="anagram-generator" />
      <div className="container" style={{maxWidth:760}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🎲</span>
          <h1>Anagram Generator</h1>
          <p>Turn your name, word, or letters into creative anagrams — including clever multi-word phrases. Perfect for name puzzles, usernames, and wordplay.</p>
        </div>

        <div className="solver-card">
          <form onSubmit={handleGenerate}>
            <div className="mb-3">
              <label className="fw-bold mb-1">Enter a Word, Name, or Letters</label>
              <input
                className="big-input"
                value={text}
                onChange={e => setText(cleanInput(e.target.value))}
                placeholder="e.g. YOUR NAME or CINEMA"
                maxLength={24}
                autoFocus
              />
              <div className="mt-2">
                <small className="text-muted fw-bold me-2">Try:</small>
                {EXAMPLES.map(ex => (
                  <button
                    key={ex} type="button"
                    className="btn btn-sm me-1 mb-1 fw-bold"
                    style={{borderRadius:'50px', background:'var(--border)', border:'none',
                            fontFamily:'Nunito,sans-serif', fontSize:'0.8rem', padding:'0.2rem 0.7rem'}}
                    onClick={() => setText(ex)}
                  >{ex}</button>
                ))}
              </div>
            </div>

            {/* Dictionary selector */}
            <div className="mb-3">
              <label className="fw-bold mb-1 d-inline-flex align-items-center gap-2">
                Dictionary <DictInfo />
              </label>
              <select className="form-select" value={dict} onChange={e => changeDict(e.target.value)}
                style={{maxWidth:360, fontWeight:700}}>
                {DICTIONARIES.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </div>

            {/* Anagram style selector */}
            <div className="mb-3">
              <label className="fw-bold mb-1 d-block">Anagram Style</label>
              <div className="d-inline-flex" style={{border:'1.5px solid #e5e7f2', borderRadius:10, overflow:'hidden'}}>
                {[['standard','✨ Standard'], ['funny','😂 Funny']].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setMode(val)}
                    className="fw-bold"
                    style={{
                      border:'none', padding:'0.45rem 1.1rem', cursor:'pointer', fontSize:'0.9rem',
                      background: mode === val ? 'var(--primary)' : '#fff',
                      color: mode === val ? '#fff' : '#64748b',
                    }}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="text-muted fw-bold mt-1" style={{fontSize:'0.8rem'}}>
                {mode === 'funny'
                  ? 'Rearranges every letter into the funniest, most sentence-like phrases.'
                  : 'Finds exact single-word anagrams plus multi-word phrases.'}
              </div>
            </div>

            {/* Multi-word toggle (Standard mode only — Funny is always multi-word) */}
            {mode === 'standard' && (
              <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" id="mw" checked={multiword}
                  onChange={e => setMulti(e.target.checked)} style={{cursor:'pointer'}}/>
                <label className="form-check-label fw-bold" htmlFor="mw" style={{cursor:'pointer'}}>
                  Include multi-word anagrams <span className="text-muted">(great for names)</span>
                </label>
              </div>
            )}

            <button type="submit" className="solve-btn" disabled={loading || !lettersOnly(text)}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Generating…</> : '🎲 Generate Anagrams'}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {data && (
          <div className="solver-card">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <span className="fw-bold" style={{fontSize:'1.1rem'}}>
                Anagrams of <span style={{color:'var(--primary)', letterSpacing:1}}>{data.input?.toUpperCase()}</span>
              </span>
              {(exact.length > 0 || phrases.length > 0) && (
                <button type="button" className="btn btn-sm fw-bold"
                  style={{borderRadius:8, background:'var(--primary)', color:'#fff', border:'none', padding:'0.3rem 0.8rem'}}
                  onClick={copyAll}>
                  {copied === '__all__' ? '✓ Copied!' : '📋 Copy all'}
                </button>
              )}
            </div>

            {/* Exact single-word anagrams */}
            {exact.length > 0 && (
              <div className="mb-3">
                <div className="fw-bold mb-2 pb-1" style={{borderBottom:'2px solid #eef0fb', color:'#334155'}}>
                  Exact Anagrams <span className="text-muted fw-bold">({exact.length})</span>
                  <span className="text-muted fw-normal" style={{fontSize:'0.82rem'}}> — same letters, different word</span>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {exact.map(r => (
                    <span key={r.word} className="d-inline-flex align-items-center"
                      style={{background:'#fff', border:'1.5px solid #e5e7f2', borderRadius:10, padding:'0.35rem 0.6rem', lineHeight:1}}>
                      <DefinableWord word={r.word} dict={dict} className="fw-bold"
                        style={{fontSize:'1rem', letterSpacing:1, textTransform:'uppercase', textDecoration:'none', color:'#1e293b'}}>
                        {r.word.toUpperCase()}
                      </DefinableWord>
                      <sup className="ms-1" style={{color:'var(--primary)', fontWeight:800, fontSize:'0.68rem'}}>{r.score}</sup>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Multi-word phrase anagrams */}
            {phrases.length > 0 && (
              <div className="mb-2">
                <div className="fw-bold mb-2 pb-1" style={{borderBottom:'2px solid #eef0fb', color:'#334155'}}>
                  {usedFunny ? '😂 Funny Anagrams' : 'Multi-Word Anagrams'} <span className="text-muted fw-bold">({phrases.length})</span>
                  <span className="text-muted fw-normal" style={{fontSize:'0.82rem'}}>
                    {usedFunny ? ' — funniest first' : ' — every letter, rearranged into a phrase'}
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {phrases.map(p => (
                    <button key={p} type="button" onClick={() => copy(p.toUpperCase(), p)}
                      title="Click to copy"
                      className="d-inline-flex align-items-center fw-bold"
                      style={{background:'#f8f9fc', border:'1.5px solid #e5e7f2', borderRadius:10,
                              padding:'0.35rem 0.7rem', color:'#1e293b', letterSpacing:0.5,
                              textTransform:'uppercase', fontSize:'0.95rem', cursor:'pointer'}}>
                      {copied === p ? '✓ Copied' : p.toUpperCase()}
                      <i className="bi bi-clipboard ms-2" style={{fontSize:'0.75rem', opacity:0.5}}></i>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Smart empty state */}
            {nothing && (
              <div className="text-center py-2">
                <div style={{fontSize:'2.5rem'}}>🧐</div>
                <p className="fw-bold mb-1">No anagrams for these exact letters.</p>
                <p className="text-muted fw-bold" style={{fontSize:'0.9rem'}}>
                  Not every word rearranges into another — but here are the longest words you can make from your letters:
                </p>
                {!usedMulti && (
                  <p className="fw-bold" style={{fontSize:'0.9rem', color:'var(--primary)'}}>
                    💡 Tip: turn on <em>“Include multi-word anagrams”</em> above to find phrases like DIRTY ROOM.
                  </p>
                )}
                {subwords.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 justify-content-center my-3">
                    {subwords.map(r => (
                      <DefinableWord key={r.word} word={r.word} dict={dict} className="result-badge"
                        style={{fontSize:'0.95rem', textDecoration:'none'}}>
                        {r.word.toUpperCase()}
                      </DefinableWord>
                    ))}
                  </div>
                )}
                <Link to="/anagram-solver" className="fw-bold" style={{color:'var(--primary)'}}>
                  Find every word in the Anagram Solver →
                </Link>
              </div>
            )}

            {(exact.length > 0 || phrases.length > 0) && (
              <p className="text-muted small fw-bold mb-0 mt-2">
                💡 Click a phrase to copy it. Click any single word to see its meaning.
              </p>
            )}
          </div>
        )}
      <SeoContent game="anagram-generator" />
      </div>
    </div>
  );
}
